import { createStore } from 'zustand/vanilla';
import { immer } from 'zustand/middleware/immer';
import { current, isDraft } from 'immer';
import { ContentMetadataState } from '../types/content-metadata-store-types';
import { ensureContentEntry } from '../utils/storeHelpers';
import { constructVideoFeeds } from '../utils/videoFeeds';
import { processSpeakerAnalysis } from '../utils/speakerAnalysis';
import { preprocessAlignedRecordings } from '../utils/alignedRecordings';
import { normalizeSpeakerDetails } from '../utils/speakerDetailsBuilders';

export function createContentMetadataStore() {
  return createStore<ContentMetadataState>()(
    immer(baseSet => {
      const set: typeof baseSet = (updater, replace?) => {
        try {
          baseSet(updater, replace);
        } catch (e) {
          console.error('[useContentMetadataStore] Error in set:', e);
        }
      };
      return {
        data: {},
        updateVideoFeeds: (contentId, videoFeeds) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].videoFeeds = videoFeeds;
          });
        },
        updateTranscript: ({ contentId, transcript }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].metadata.transcript = transcript;
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        updateSpeakerAnalysis: ({ contentId, speakerAnalysis }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            const startTime = state.data[contentId].metadata.sessionDetails?.startTime;
            const startTimeValue = isDraft(startTime) ? current(startTime) : startTime;
            const processedAnalysis =
              startTimeValue !== undefined ? processSpeakerAnalysis(speakerAnalysis, startTimeValue) : speakerAnalysis;
            state.data[contentId].metadata.speakerAnalysis = processedAnalysis;
            state.data[contentId].loading.speakerAnalysisLoading = false;
            const endTime = state.data[contentId].metadata.sessionDetails?.endTime;
            const endTimeValue = isDraft(endTime) ? current(endTime) : endTime;
            if (
              state.data[contentId].loading.videoFeedsLoading ||
              endTimeValue === undefined ||
              startTimeValue === undefined
            ) {
              return;
            }
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        updateSpeakerDetails: ({ contentId, speakerDetails }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].metadata.speakerDetails = normalizeSpeakerDetails(speakerDetails);
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        updateAlignedRecording: ({ contentId, alignedRecordings }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            const usePureCloudRecording = state.data[contentId].metadata.sessionDetails?.usePureCloudRecording;
            state.data[contentId].metadata.alignedRecordings = preprocessAlignedRecordings({
              recordings: alignedRecordings,
              usePureCloudRecording
            });
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        initContentMediaSource: ({ contentId, clip }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].metadata.sessionDetails = {
              contentMediaSource: clip.content.media_source_type,
              isIndividualRecordingComplete: clip.content.individual_recording_status === 'DONE',
              eventType: clip.content.event_type ?? null,
              broadcastType: clip.content.broadcast_type,
              originalVideoSource: {
                preview: clip.content.preview,
                source: clip.content.source
              },
              maxVideoDuration: clip.asset_metadata?.maxVideoDuration ?? clip.asset_metadata?.end,
              startTime: clip.asset_metadata?.start,
              endTime: clip.asset_metadata?.end,
              usePureCloudRecording: clip.content.use_pure_cloud_recording
            };
            state.data[contentId].loading.videoFeedsLoading = false;
            const speakerAnalysis = state.data[contentId].metadata.speakerAnalysis;
            const speakerAnalysisValue = isDraft(speakerAnalysis) ? current(speakerAnalysis) : speakerAnalysis;
            if (speakerAnalysisValue && clip.asset_metadata?.start !== undefined) {
              state.data[contentId].metadata.speakerAnalysis = processSpeakerAnalysis(
                speakerAnalysisValue,
                clip.asset_metadata?.start ?? 0
              );
            }
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        removeContentMetadata: contentId => {
          set(state => {
            delete state.data[contentId];
          });
        },
        setSpeakerAnalysisLoading: (contentId, isLoading) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].loading.speakerAnalysisLoading = isLoading;
            if (isLoading) {
              state.data[contentId].loading.videoFeedsLoading = true;
            }
          });
        },

        // Not to be used directly for now. Should be used once ClipContextProvider and TranscriptContextProvider are merged.
        setVideoFeedsLoading: (contentId, isLoading) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].loading.videoFeedsLoading = isLoading;
          });
        },

        // These should be replaced in future by only setVideoFeedsLoading once Transcript fetching is moved into ClipContextProvider.
        setClipDataLoaded: ({ contentId, isLoaded }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].loading.clipDataLoaded = isLoaded;
            state.data[contentId].loading.videoFeedsLoading =
              isLoaded || state.data[contentId].loading.transcriptLoading;
            if (state.data[contentId].loading.videoFeedsLoading) {
              return;
            }
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        },
        setTranscriptLoading: ({ contentId, isLoading }) => {
          set(state => {
            ensureContentEntry(state.data, contentId);
            state.data[contentId].loading.transcriptLoading = isLoading;
            state.data[contentId].loading.videoFeedsLoading = state.data[contentId].loading.clipDataLoaded || isLoading;
            if (isLoading) {
              return;
            }
            state.data[contentId].videoFeeds = constructVideoFeeds({
              contentMetadataStoreEntry: isDraft(state.data[contentId])
                ? current(state.data[contentId])
                : state.data[contentId]
            });
          });
        }
      };
    })
  );
}

export type ContentMetadataStoreFactoryType = ReturnType<typeof createContentMetadataStore>;
